import React, { useState, useEffect } from "react";
import type {
  Veille,
  ScreenSaver,
  InfoChannel,
  SlideShow,
  PersonDetection
} from '../../model/Veille'
import useVeille from '../../redux/Hooks/Kiosk/useVeille';
import { veilleChangeAction } from "../../redux/Actions";
import { useDispatchKiosk } from "../../redux/Hooks/Kiosk/useDispatchKiosk";
import { useLocale } from "@davi-ai/retorik-framework";
import translation from '../../translations/subMenuItems.json';
import commonTranslation from '../../translations/common.json';
import ScreenSaverData from './Veille/ScreenSaver';
import SlideShowData from './Veille/SlideShow';
import ChooseItem from './ChooseItem';
import Button from "../Common/Button";
import TextField from "./TextField";
import OnOffButton from './OnOffButtons';
import Underline from "./Underline";
import { defaultVeilleTime } from '../../model/Configuration'
import InputNumber from "./InputNumber";

interface InputData {
  id: string
  label: string
}

const VeilleChoice = () => {
  const locale = useLocale();
  const dispatch = useDispatchKiosk()
  const veille = useVeille()
  const [time, setTime] = useState<number | undefined>(veille?.timer || defaultVeilleTime)
  const [currentItem, setCurrentItem] = useState<string | undefined>(undefined)
  const [veilleOn, setVeilleOn] = useState<boolean>(veille?.activated || false)
  const [veilleMuted, setVeilleMuted] = useState<boolean>(veille?.muted || false)
  const [screenSaverData, setScreenSaverData] = useState<ScreenSaver | undefined>(veille?.screenSaver)
  const [infoChannelData, setInfoChannelData] = useState<InfoChannel | undefined>(veille?.infoChannel)
  const [slideShowData, setSlideShowData] = useState<SlideShow | undefined>(veille?.slideShow)
  const [personDetectionData, setPersonDetectionData] = useState<PersonDetection>(veille?.personDetection || {
    activated: false,
    webcamId: undefined,
    minDistance: 0,
    maxDistance: 150,
    normalizedFocaleX: 1.40625
  })
  const [inputs, setInputs] = useState<{ front: InputData | undefined, back: InputData | undefined } | undefined>(undefined);

  useEffect(() => {
    const checkMedia = async () => {
      const frontWebcam = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }})
        .then((device) => {
          const track = device.getVideoTracks();
          return track.length > 0 ?
          {
            id: track[0].id,
            label: track[0].label
          } : undefined;
        })
        .catch((error) => {
          return undefined;
        });

      const backWebcam = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: "environment" }}})
        .then((device) => {
          const track = device.getVideoTracks();
          return track.length > 0 ?
          {
            id: track[0].id,
            label: track[0].label
          } : undefined;
        })
        .catch((error) => {
          return undefined;
        });
        
      setInputs({ front: frontWebcam, back: backWebcam })
    }

    checkMedia();
  }, []);

  const handleTimeChange = (event: any) => {
    if (event.currentTarget.value === '') {
      setTime(undefined)
    } else {
      const val = parseInt(event.currentTarget.value);
      val && setTime(parseInt(event.currentTarget.value));
    }
  }

  const toggleVeille = () => {
    setVeilleOn(!veilleOn)
  }

  const toggleMuteVeille = () => {
    setVeilleMuted(!veilleMuted)
  }

  const toggleScreenSaver = () => {
    const temp = screenSaverData?.activated || false;
    screenSaverData?.source ?
      setScreenSaverData({
        ...screenSaverData,
        activated: !temp
      })
      :
      setScreenSaverData({
        ...screenSaverData,
        activated: !temp,
        source: ''
      })
  }

  const toggleInfoChannel = () => {
    const temp = infoChannelData?.activated || false;
    setInfoChannelData({ activated: !temp });
  }

  const toggleSlideShow = () => {
    const temp = slideShowData?.activated || false;
    slideShowData?.sources ?
      setSlideShowData({
        ...slideShowData,
        activated: !temp
      })
      :
      setSlideShowData({
        ...slideShowData,
        activated: !temp,
        sources: []
      })
  }

  const togglePersonDetection = () => {
    const temp = personDetectionData?.activated || false;
    setPersonDetectionData({
      ...personDetectionData,
      activated: !temp
    });
  }

  const handleChooseWebcam = (webcamId?: string) => {
    webcamId && 
      setPersonDetectionData({
        ...personDetectionData,
        webcamId: webcamId
      });
  }

  const handleMinDistanceChange = (event: any) => {
    if (event.currentTarget.value === '') {
      setPersonDetectionData({
        ...personDetectionData,
        minDistance: 0
      });
    } else {
      const val = parseInt(event.currentTarget.value);
      val && val >= 0 && setPersonDetectionData({
        ...personDetectionData,
        minDistance: val
      });
    }
  }

  const handleMaxDistanceChange = (event: any) => {
    if (event.currentTarget.value === '') {
      setPersonDetectionData({
        ...personDetectionData,
        maxDistance: 150
      });
    } else {
      const val = parseInt(event.currentTarget.value);
      val && val >= 0 && setPersonDetectionData({
        ...personDetectionData,
        maxDistance: val
      });
    }
  }

  const handleNormalizedFocaleChange = (event: any) => {
    if (event.currentTarget.value === '') {
      setPersonDetectionData({
        ...personDetectionData,
        normalizedFocaleX: 1.40625
      });
    } else {
      const val = parseFloat(event.currentTarget.value);
      val && val >= 0 && setPersonDetectionData({
        ...personDetectionData,
        normalizedFocaleX: val
      });
    }
  }

  const save = () => {
    var tempTime: number | undefined = time;
    if(time && time <= 10) {
      tempTime = 10;
      setTime(10);
    }

    const temp: Veille = {
      activated: veilleOn,
      muted: veilleMuted,
      timer: tempTime || defaultVeilleTime,
      screenSaver: screenSaverData,
      infoChannel: infoChannelData,
      slideShow: slideShowData,
      personDetection: personDetectionData
    }

    !time && setTime(defaultVeilleTime);

    dispatch(veilleChangeAction(temp));
  }

  return (
    <div className="w-full h-9/10 flex flex-col justify-between">
      <div className="w-full flex flex-col gap-4 mt-6 px-1">
        <div className="flex flex-row justify-between items-center">
          {translation[locale]?.veille.veille || translation["fr-FR"].veille.veille}
          <OnOffButton
            toggle={veilleOn}
            returnFunction={toggleVeille}
          />
        </div>

        <Underline />

        {veilleOn &&
          <React.Fragment>
            {/* Mute veille */}
            <div className='flex flex-row justify-between items-center my-1'>
              {translation[locale]?.veille.mute || translation["fr-FR"].veille.mute}
              <OnOffButton
                toggle={veilleMuted}
                returnFunction={toggleMuteVeille}
              />
            </div>

            {/* Timer */}
            <TextField
              label={translation[locale]?.veille.delay || translation["fr-FR"].veille.delay}
              onChange={handleTimeChange}
              value={time}
            />

            <div className='w-full flex flex-col'>
              {/* ScreenSaver */}
              <div className='flex flex-row justify-between items-center my-1'>
                {translation[locale]?.veille.screensaver || translation["fr-FR"].veille.screensaver}
                <OnOffButton
                  toggle={screenSaverData?.activated || false}
                  returnFunction={toggleScreenSaver}
                />
              </div>
              <ChooseItem
                current={currentItem === 'screensaver'}
                item="screensaver"
                setItem={setCurrentItem}
              />
              {currentItem === 'screensaver' && <ScreenSaverData
                data={screenSaverData}
                saveData={setScreenSaverData}
              />}

              <Underline />

              {/* News */}
              <div className='flex flex-row justify-between items-center my-1'>
                {translation[locale]?.veille.news || translation["fr-FR"].veille.news}
                <OnOffButton
                  toggle={infoChannelData?.activated || false}
                  returnFunction={toggleInfoChannel}
                />
              </div>

              <Underline />

              {/* SlideShow */}
              <div className='flex flex-row justify-between items-center my-1'>
                {translation[locale]?.veille.slideshow || translation["fr-FR"].veille.slideshow}
                <OnOffButton
                  toggle={slideShowData?.activated || false}
                  returnFunction={toggleSlideShow}
                />
              </div>
              <ChooseItem
                current={currentItem === 'slideshow'}
                item="slideshow"
                setItem={setCurrentItem}
              />
              {currentItem === 'slideshow' && <SlideShowData
                data={slideShowData}
                saveData={setSlideShowData}
              />}

              <Underline />

              {/* Person detection */}
              {inputs && (inputs.front || inputs.back) && (
                <React.Fragment>
                  <div className='flex flex-row justify-between items-center my-1'>
                    {translation[locale]?.veille.persondetection || translation["fr-FR"].veille.persondetection}
                    <OnOffButton
                      toggle={personDetectionData?.activated || false}
                      returnFunction={togglePersonDetection}
                    />
                  </div>
                  <ChooseItem
                    current={currentItem === 'persondetection'}
                    item="persondetection"
                    setItem={setCurrentItem}
                  />
                  {personDetectionData?.activated && currentItem === 'persondetection' && (
                    <React.Fragment>
                      {/* Webcam choice */}
                      <div className="w-full flex flex-col gap-1 my-1 px-4">
                        {translation[locale]?.veille.persondetectionwebcam || translation["fr-FR"].veille.persondetectionwebcam} :
                        {inputs.front && (
                            <Button onClick={() => handleChooseWebcam(inputs.front?.id)} label="Choose">
                              (Front) {inputs.front.label}
                            </Button>
                          )
                        }
                        {inputs.back && (
                            <Button onClick={() => handleChooseWebcam(inputs.back?.id)} label="Choose">
                              (Rear) {inputs.back.label}
                            </Button>
                          )
                        }
                      </div>

                      {/* Minimal distance to trigger standby exit */}
                      <div className="w-full flex flex-col gap-1 my-1 px-4">
                        <InputNumber
                          label={translation[locale]?.veille.persondetectionmin || translation["fr-FR"].veille.persondetectionmin}
                          onChange={handleMinDistanceChange}
                          value={personDetectionData.minDistance}
                          placeholder={translation[locale]?.veille.persondetectionminplaceholder || translation["fr-FR"].veille.persondetectionminplaceholder}
                        />
                      </div>

                      {/* Maximal distance to trigger standby exit */}
                      <div className="w-full flex flex-col gap-1 my-1 px-4">
                        <InputNumber
                          label={translation[locale]?.veille.persondetectionmax || translation["fr-FR"].veille.persondetectionmax}
                          onChange={handleMaxDistanceChange}
                          value={personDetectionData.maxDistance}
                          placeholder={translation[locale]?.veille.persondetectionmaxplaceholder || translation["fr-FR"].veille.persondetectionmaxplaceholder}
                        />
                      </div>

                      {/* Normalized focale */}
                      <div className="w-full flex flex-col gap-1 my-1 px-4">
                        <InputNumber
                          label={translation[locale]?.veille.persondetectionnormalizedfocale || translation["fr-FR"].veille.persondetectionnormalizedfocale}
                          onChange={handleNormalizedFocaleChange}
                          value={personDetectionData.normalizedFocaleX}
                          placeholder={translation[locale]?.veille.persondetectionnormalizedfocaleplaceholder || translation["fr-FR"].veille.persondetectionnormalizedfocaleplaceholder}
                          step="any"
                        />
                      </div>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}

            </div>
          </React.Fragment>
        }
      </div>

      <div className="mb-2 mt-1 flex justify-center">
        <Button label={commonTranslation[locale]?.save || commonTranslation["fr-FR"].save} onClick={save}>
          {commonTranslation[locale]?.save || commonTranslation["fr-FR"].save}
        </Button>
      </div>
    </div>
  );
};

export default VeilleChoice;
